import React from "react";
import {reverse} from "@reactivated";
import {Layout} from "../Layout";

type Freshness = {
    iso: string;
    absolute: string;
    relative: string;
};

type SearchItem = {
    song_uid: string;
    url: string;
    title: string;
    author: string;
    lyric_preview: string[];
    lyrics_available: boolean;
    rights_status: string;
    song_freshness: Freshness;
};

type CatalogSearchPageProps = {
    title: string;
    query: string;
    mode: string;
    limit: number;
    searched: boolean;
    results: SearchItem[];
    catalog_freshness: Freshness | null;
    next_url: string | null;
    has_more: boolean;
    error: string | null;
    restart_url: string | null;
};

type RecentSearch = {
    query: string;
    mode: "title" | "lyrics";
};

const RECENT_SEARCHES_KEY = "wpp.catalog.recent-searches.v1";
const SEARCH_WORD_PATTERN = /[\p{L}\p{N}_]+/gu;

const normalizeSearchWord = (value: string) => (
    value.normalize("NFKD").replace(/\p{M}/gu, "").toLocaleLowerCase()
);

const HighlightedLyric = ({line, query}: {line: string; query: string}) => {
    const terms = new Set(
        Array.from(query.matchAll(SEARCH_WORD_PATTERN), ([word]) => normalizeSearchWord(word)),
    );
    if (terms.size === 0) return <>{line}</>;

    const content: React.ReactNode[] = [];
    let lastIndex = 0;
    for (const match of line.matchAll(SEARCH_WORD_PATTERN)) {
        const word = match[0];
        const start = match.index;
        if (start > lastIndex) content.push(line.slice(lastIndex, start));
        if (terms.has(normalizeSearchWord(word))) {
            content.push(
                <mark
                    key={`${start}-${word}`}
                    className="-mx-px bg-worship-accent-200 px-px font-semibold text-chapel-neutral-950 underline decoration-worship-accent-700 decoration-1 underline-offset-2"
                >
                    {word}
                </mark>,
            );
        } else {
            content.push(word);
        }
        lastIndex = start + word.length;
    }
    if (lastIndex < line.length) content.push(line.slice(lastIndex));
    return <>{content}</>;
};

const normalizeRecentQuery = (query: string) => (
    query.trim().replace(/\s+/gu, " ").normalize("NFKC")
);

const getRecentSearchKey = (search: RecentSearch) => (
    `${search.mode}:${search.query.toLowerCase()}`
);

const validateRecentSearches = (value: unknown): RecentSearch[] => {
    if (!Array.isArray(value)) return [];
    const searches: RecentSearch[] = [];
    const seen = new Set<string>();
    for (const item of value) {
        if (
            typeof item !== "object"
            || item === null
            || !("query" in item)
            || typeof item.query !== "string"
            || !("mode" in item)
            || (item.mode !== "title" && item.mode !== "lyrics")
        ) continue;
        const search: RecentSearch = {
            query: normalizeRecentQuery(item.query),
            mode: item.mode,
        };
        const key = getRecentSearchKey(search);
        if (search.query.length === 0 || search.query.length > 128 || seen.has(key)) continue;
        searches.push(search);
        seen.add(key);
        if (searches.length === 5) break;
    }
    return searches;
};

const SearchGlyph = () => (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="11" cy="11" r="7" />
        <path d="m16.2 16.2 4.1 4.1" />
    </svg>
);

const FreshnessNote = ({freshness}: {freshness: Freshness | null}) => (
    <p className="flex items-center justify-end gap-3 text-end text-xs font-bold uppercase tracking-[0.14em] text-chapel-neutral-600">
        <span className="h-2 w-2 rounded-full bg-chapel-primary-500" aria-hidden="true" />
        {freshness ? (
            <span>
                Catalog refreshed <time dateTime={freshness.iso} title={freshness.absolute}>{freshness.relative}</time>
            </span>
        ) : (
            <span>Catalog awaiting its first import</span>
        )}
    </p>
);

const SearchForm = ({props}: {props: CatalogSearchPageProps}) => (
    <form action={reverse("catalog:search")} method="get" role="search">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
                <label htmlFor="catalog-query" className="text-xs font-bold uppercase tracking-[0.16em] text-chapel-neutral-600">
                    Search titles or lyrics
                </label>
                <p className="mt-2 font-serif text-base italic leading-6 text-chapel-neutral-600">
                    Search by name when you know it; search by lyrics when only the words remain.
                </p>
            </div>
            <FreshnessNote freshness={props.catalog_freshness} />
        </div>
        <div className="mt-3 flex items-end gap-3 border-b-2 border-chapel-neutral-950 focus-within:border-chapel-primary-500">
            <input
                id="catalog-query"
                type="search"
                name="q"
                defaultValue={props.query}
                maxLength={128}
                autoComplete="off"
                placeholder={props.mode === "lyrics" ? "Enter a line you remember" : "Enter a song title"}
                className="min-w-0 flex-1 border-0 bg-transparent py-4 font-serif text-2xl text-chapel-neutral-950 outline-none placeholder:text-chapel-neutral-400 sm:text-3xl"
                aria-describedby="catalog-search-help"
            />
            <button
                type="submit"
                className="mb-3 grid h-11 w-11 shrink-0 place-items-center border border-chapel-neutral-950 text-chapel-neutral-950 transition-colors hover:border-chapel-primary-500 hover:bg-chapel-primary-500 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-chapel-primary-500"
                aria-label="Search the song catalog"
            >
                <SearchGlyph />
            </button>
        </div>
        <fieldset className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
            <legend className="sr-only">Search in</legend>
            {(["title", "lyrics"] as const).map((mode) => (
                <label key={mode} className="flex cursor-pointer items-center gap-2 text-xs font-bold uppercase tracking-[0.14em]">
                    <input
                        type="radio"
                        name="mode"
                        value={mode}
                        defaultChecked={props.mode === mode}
                        className="h-4 w-4 accent-chapel-primary-500"
                    />
                    {mode}
                </label>
            ))}
        </fieldset>
        {props.limit !== 20 && <input type="hidden" name="limit" value={props.limit} />}
        <p id="catalog-search-help" className="mt-4 max-w-xl text-sm leading-6 text-chapel-neutral-600">
            Use ordinary words. Capitalization and accents do not affect results.
        </p>
    </form>
);

const SearchHints = () => (
    <div className="grid gap-px border border-chapel-neutral-300 bg-chapel-neutral-300 sm:grid-cols-3">
        {[
            ["Title fragment", "Try two or three words from the song name."],
            ["Lyric line", "Choose Lyrics when the words are more familiar than the title."],
            ["Plain language", "No operators or special search syntax are needed."],
        ].map(([heading, body]) => (
            <article key={heading} className="bg-chapel-neutral-50 p-5">
                <h3 className="font-sans text-xs font-bold uppercase tracking-[0.14em]">{heading}</h3>
                <p className="mt-2 text-sm leading-6 text-chapel-neutral-600">{body}</p>
            </article>
        ))}
    </div>
);

const RecentSearches = ({query, mode, shouldRecord}: {query: string; mode: string; shouldRecord: boolean}) => {
    const [searches, setSearches] = React.useState<RecentSearch[]>([]);

    React.useEffect(() => {
        let stored: RecentSearch[] = [];
        try {
            stored = validateRecentSearches(JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) ?? "[]"));
        } catch {}

        const current: RecentSearch = {
            query: normalizeRecentQuery(query),
            mode: mode === "lyrics" ? "lyrics" : "title",
        };
        const canRecord = shouldRecord && current.query.length > 0 && current.query.length <= 128;
        const currentKey = getRecentSearchKey(current);
        const next = canRecord
            ? [current, ...stored.filter((item) => getRecentSearchKey(item) !== currentKey)].slice(0, 5)
            : stored;
        setSearches(next);

        if (canRecord) {
            try {
                localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
            } catch {}
        }
    }, [query, mode, shouldRecord]);

    const clearSearches = () => {
        try {
            localStorage.removeItem(RECENT_SEARCHES_KEY);
        } catch {}
        setSearches([]);
    };

    if (searches.length === 0) return null;

    return (
        <div className="mt-10 border-t border-white/30 pt-6">
            <div className="flex items-center justify-between gap-4">
                <h2 className="font-sans text-xs font-bold uppercase tracking-[0.14em] text-worship-accent-200">Recent searches</h2>
                <button
                    type="button"
                    onClick={clearSearches}
                    className="border-b border-white/40 pb-0.5 text-[0.65rem] font-bold uppercase tracking-[0.12em] text-white/70 transition-colors hover:border-worship-accent-200 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-worship-accent-200"
                    aria-label="Clear recent searches from this browser"
                >
                    Clear
                </button>
            </div>
            <ul className="mt-3 divide-y divide-white/20">
                {searches.map((search) => (
                    <li key={`${search.mode}:${search.query}`}>
                        <a
                            href={`${reverse("catalog:search")}?q=${encodeURIComponent(search.query)}&mode=${search.mode}`}
                            className="group flex items-center justify-between gap-3 py-3 text-sm text-white/85 transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-worship-accent-200"
                        >
                            <span className="min-w-0 truncate">{search.query}</span>
                            <span className="shrink-0 text-[0.65rem] font-bold uppercase tracking-[0.12em] text-white/55 group-hover:text-worship-accent-200">{search.mode}</span>
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export const Template = (props: CatalogSearchPageProps) => {
    const [displayedResults, setDisplayedResults] = React.useState(props.results);
    const [nextUrl, setNextUrl] = React.useState(props.next_url);
    const [hasMore, setHasMore] = React.useState(props.has_more);
    const [isLoadingNext, setIsLoadingNext] = React.useState(false);

    // Reset state if props change (e.g. user performs a new search)
    React.useEffect(() => {
        setDisplayedResults(props.results);
        setNextUrl(props.next_url);
        setHasMore(props.has_more);
    }, [props.results, props.next_url, props.has_more]);

    const handleLoadMore = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (!nextUrl || isLoadingNext) return;

        setIsLoadingNext(true);
        try {
            const response = await fetch(nextUrl, {
                headers: {
                    "Accept": "application/json",
                },
            });
            if (response.ok) {
                const data = await response.json() as {props: CatalogSearchPageProps};
                setDisplayedResults((prev) => [...prev, ...data.props.results]);
                setNextUrl(data.props.next_url);
                setHasMore(data.props.has_more);
            }
        } catch (error) {
            console.error("Failed to load more results", error);
        } finally {
            setIsLoadingNext(false);
        }
    };

    const resultLabel = displayedResults.length === 1 ? "1 song" : `${displayedResults.length} songs`;

    return (
        <Layout title={props.title}>
            <main className="min-h-[calc(100dvh-5rem)] bg-chapel-neutral-50 text-chapel-neutral-950 overflow-x-hidden">
                <div className="grid w-full lg:grid-cols-[20rem_minmax(0,1fr)] xl:grid-cols-[24rem_minmax(0,1fr)]">
                    <aside className="min-w-0 border-b border-chapel-primary-700 bg-chapel-primary-500 p-7 text-white lg:sticky lg:top-20 lg:h-[calc(100dvh-5rem)] lg:self-start lg:overflow-y-auto lg:border-b-0 lg:border-e lg:p-10">
                        <h1 className="max-w-xs text-5xl leading-[0.9] text-balance">Songs for worship.</h1>
                        <p className="mt-6 max-w-md text-sm leading-6 text-white/80">
                            A catalogue of easy worship songs updated weekly.
                        </p>
                        <RecentSearches
                            query={props.query}
                            mode={props.mode}
                            shouldRecord={props.searched && props.error === null}
                        />
                    </aside>

                    <section className="min-w-0 px-6 py-9 sm:px-8 lg:flex lg:h-[calc(100dvh-5rem)] lg:flex-col lg:overflow-hidden lg:px-12 lg:py-0 2xl:px-24" aria-labelledby="catalog-results-heading">
                        <div className="border-b border-chapel-neutral-300 pb-9 lg:shrink-0 lg:pt-10">
                            <div className="mx-auto max-w-5xl">
                                <SearchForm props={props} />
                                <div className="mt-5 flex flex-wrap items-center gap-x-2">
                                    <p className="text-sm text-chapel-neutral-700">Can’t find your song?</p>
                                    <a href={`${reverse("song_carts:add_unlisted")}${props.mode === "title" && props.query.trim() ? `?title=${encodeURIComponent(props.query.trim())}` : ""}`} className="inline-flex min-h-11 items-center text-sm font-bold text-chapel-primary-600 underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-chapel-primary-500">Add an unlisted song</a>
                                </div>
                            </div>
                        </div>

                        <div className="wrap-anywhere pt-10 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:scroll-py-6 lg:pb-14 lg:pr-4">
                            <div className="mx-auto max-w-5xl">
                            {props.error ? (
                                <div role="alert" className="border border-chapel-primary-200 bg-chapel-primary-50 p-6 sm:p-8">
                                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-chapel-primary-500">Search needs attention</p>
                                    <h2 id="catalog-results-heading" className="mt-3 text-3xl">We could not continue that search.</h2>
                                    <p className="mt-3 max-w-2xl text-chapel-neutral-700">{props.error}</p>
                                    {props.restart_url && (
                                        <a href={props.restart_url} className="mt-6 inline-flex border-b border-chapel-neutral-950 pb-1 text-xs font-bold uppercase tracking-[0.14em]">
                                            Restart this search
                                        </a>
                                    )}
                                </div>
                            ) : !props.searched ? (
                                <div>
                                    <div className="mb-7 flex flex-col gap-2 border-b border-chapel-neutral-950 pb-4 sm:flex-row sm:items-end sm:justify-between">
                                        <h2 id="catalog-results-heading" className="text-4xl">Start with what you remember.</h2>
                                        <p className="text-sm text-chapel-neutral-500">Three ways to begin</p>
                                    </div>
                                    <SearchHints />
                                </div>
                            ) : props.results.length === 0 ? (
                                <div className="max-w-2xl py-8">
                                    <p className="font-serif text-6xl italic text-chapel-primary-500" aria-hidden="true">0</p>
                                    <h2 id="catalog-results-heading" className="mt-4 text-4xl">No songs found for “{props.query.trim()}.”</h2>
                                    <p className="mt-4 leading-7 text-chapel-neutral-600">
                                        Try fewer words, check the spelling, or switch between Title and Lyrics. Your search remains above.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex flex-col gap-3 border-b border-chapel-neutral-950 pb-4 sm:flex-row sm:items-end sm:justify-between">
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-[0.14em] text-chapel-primary-500">Searching {props.mode}</p>
                                            <h2 id="catalog-results-heading" className="mt-2 text-4xl">Songs for “{props.query.trim()}”</h2>
                                        </div>
                                            <p className="shrink-0 text-sm text-chapel-neutral-500">Showing {resultLabel}{hasMore ? " on this page" : ""}</p>
                                        </div>

                                        <div className="divide-y divide-chapel-neutral-300">
                                            {displayedResults.map((song, index) => (
                                            <article key={song.song_uid} className="group grid gap-6 py-6 sm:grid-cols-[2.5rem_minmax(0,1.2fr)_minmax(15rem,1fr)_2rem] sm:items-center">
                                                <span className="font-serif text-lg italic text-chapel-neutral-400" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                                                <div className="min-w-0 pr-6">
                                                    <h3 className="text-3xl leading-none">
                                                        <a href={song.url} className="decoration-chapel-primary-300 underline-offset-8 transition-colors hover:text-chapel-primary-500 hover:underline focus-visible:underline">
                                                            {song.title}
                                                        </a>
                                                    </h3>
                                                    <p className="mt-2 text-sm font-semibold text-chapel-neutral-700">Author: {song.author}</p>
                                                    <p className="mt-1 text-xs text-chapel-neutral-500">
                                                        Updated <time dateTime={song.song_freshness.iso} title={song.song_freshness.absolute}>{song.song_freshness.relative}</time>
                                                    </p>
                                                    <a href={reverse("song_carts:add", {song_uid: song.song_uid})} className="mt-3 inline-flex min-h-10 items-center text-sm font-semibold text-chapel-primary-500 underline underline-offset-4" aria-label={`Add ${song.title} to a song cart`}>
                                                        Add to song cart
                                                    </a>
                                                </div>
                                                <div className="min-w-0 border-s border-chapel-neutral-300 pl-6 text-sm leading-6 text-chapel-neutral-600">
                                                    {song.lyrics_available ? (
                                                        <>
                                                            <p className="mb-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-chapel-neutral-400">Lyric preview</p>
                                                            {song.lyric_preview.map((line, lineIndex) => (
                                                                <p key={lineIndex} className="break-words">
                                                                    {props.mode === "lyrics" ? (
                                                                        <HighlightedLyric line={line} query={props.query} />
                                                                    ) : line}
                                                                </p>
                                                            ))}
                                                        </>
                                                    ) : (
                                                        <p className="italic text-chapel-neutral-500">Lyrics unavailable for public display.</p>
                                                    )}
                                                </div>
                                                <span className="hidden text-right text-xl text-chapel-primary-500 transition-transform group-hover:translate-x-1 sm:block" aria-hidden="true">→</span>
                                            </article>
                                        ))}
                                    </div>

                                    {nextUrl && (
                                        <div className="flex justify-end border-t border-chapel-neutral-950 pt-7">
                                            <a 
                                                href={nextUrl} 
                                                rel="next" 
                                                data-purpose="catalog-next" 
                                                className={`minimal-btn ${isLoadingNext ? "opacity-50 pointer-events-none" : ""}`}
                                                onClick={handleLoadMore}
                                            >
                                                {isLoadingNext ? "Loading..." : "Load more"} <span aria-hidden="true" className="ml-3">↓</span>
                                            </a>
                                        </div>
                                    )}
                                </>
                            )}
                            </div>
                        </div>
                    </section>
                </div>
            </main>
        </Layout>
    );
};
